import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  FormField,
  Input,
  Modal,
  Surface,
  Table,
  Td,
  Th,
  Tr,
} from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProductionOrderComponent } from '@warehouse/shared';
import { ArrowLeft, ClipboardList, Plus, Trash2, Truck } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import {
  cancelOrder,
  completeOrder,
  fetchOrder,
  fetchTraceability,
  planStaging,
  productionKeys,
  releaseOrder,
  setComponents,
  stageComponent,
  startOrder,
} from '../api';
import { ArticlePicker } from '../components/Pickers';
import { PRODUCTION_ORDER_TONES, orderActionsFor, producedPercent } from '../lib/status';
import { PageHeader } from '../../../components/PageHeader';
import { FullPageSpinner } from '../../../components/FullPageSpinner';
import { usePermission } from '../../../auth/auth-store';
import { formatDate, formatDateTime, formatQty } from '../../../lib/format';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

/**
 * One production order, end to end: plan → components → goods to production →
 * release → report output → the batch trace that comes out the other side.
 */
export function ProductionOrderDetailPage(): ReactNode {
  const { t } = useTranslation('production');
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const canManage = usePermission('production.orders.manage');
  const canReport = usePermission('production.orders.report');

  const [completing, setCompleting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [staging, setStaging] = useState<ProductionOrderComponent | null>(null);
  const [editingComponents, setEditingComponents] = useState(false);

  const order = useQuery({ queryKey: productionKeys.order(id), queryFn: () => fetchOrder(id), enabled: Boolean(id) });
  const trace = useQuery({
    queryKey: productionKeys.traceability(id),
    queryFn: () => fetchTraceability(id),
    enabled: Boolean(id),
  });
  useLiveQuery(['production', 'stock'], productionKeys.all);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: productionKeys.all });
  };

  const transition = useMutation({
    mutationFn: ({ action }: { action: 'release' | 'start' | 'staging' }) =>
      action === 'release' ? releaseOrder(id) : action === 'start' ? startOrder(id) : planStaging(id),
    onSuccess: (_result, variables) => {
      toastSuccess(
        variables.action === 'release'
          ? t('orders.toasts.released')
          : variables.action === 'start'
            ? t('orders.toasts.started')
            : t('orders.toasts.stagingPlanned'),
      );
      refresh();
    },
    onError: (err) => {
      toastError(err);
    },
  });

  if (order.isLoading) return <FullPageSpinner />;
  if (order.isError || !order.data) {
    return <PageHeader title={t('orders.title')} subtitle={t('orders.empty')} />;
  }

  const data = order.data;
  const actions = orderActionsFor(data.status);
  const percent = producedPercent(data.qtyBase, data.producedQtyBase);

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link to="/production/orders" className="inline-flex items-center gap-1 hover:text-ink">
            <ArrowLeft className="size-3.5" aria-hidden />
            {t('orders.actions.back')}
          </Link>
        }
        title={data.orderNo}
        subtitle={`${data.outputArticleNo} · ${data.outputArticleName}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={PRODUCTION_ORDER_TONES[data.status]} dot>
              {t(`orders.status.${data.status}`)}
            </Badge>
            {canManage && actions.canPlanStaging ? (
              <Button
                variant="secondary"
                icon={<ClipboardList className="size-4" />}
                loading={transition.isPending}
                onClick={() => {
                  transition.mutate({ action: 'staging' });
                }}
              >
                {t('orders.actions.planStaging')}
              </Button>
            ) : null}
            {canManage && actions.canRelease ? (
              <Button
                loading={transition.isPending}
                onClick={() => {
                  transition.mutate({ action: 'release' });
                }}
              >
                {t('orders.actions.release')}
              </Button>
            ) : null}
            {canManage && actions.canStart ? (
              <Button
                variant="secondary"
                loading={transition.isPending}
                onClick={() => {
                  transition.mutate({ action: 'start' });
                }}
              >
                {t('orders.actions.start')}
              </Button>
            ) : null}
            {canReport && actions.canComplete ? (
              <Button
                onClick={() => {
                  setCompleting(true);
                }}
              >
                {t('orders.actions.complete')}
              </Button>
            ) : null}
            {canManage && actions.canCancel ? (
              <Button
                variant="danger"
                onClick={() => {
                  setCancelling(true);
                }}
              >
                {t('orders.actions.cancel')}
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="flex flex-col gap-6">
        <Surface className="grid grid-cols-2 gap-x-6 gap-y-4 p-5 md:grid-cols-4">
          <Field label={t('orders.columns.qty')}>
            {formatQty(data.qtyBase, { unit: data.unitCode ?? undefined })}
          </Field>
          <Field label={t('orders.columns.produced')}>
            {formatQty(data.producedQtyBase)} <span className="text-ink-muted">({percent}%)</span>
          </Field>
          <Field label={t('orders.columns.plannedStart')}>
            {data.plannedStart ? formatDate(data.plannedStart) : '—'}
          </Field>
          <Field label={t('orders.detail.floorBin')}>
            {data.floorBinCode ?? (
              <span className="text-ink-muted">{t('orders.detail.floorBinMissing')}</span>
            )}
          </Field>
          {data.note ? (
            <div className="col-span-2 md:col-span-4">
              <Field label={t('orders.form.note')}>{data.note}</Field>
            </div>
          ) : null}
        </Surface>

        <ComponentsCard
          orderId={id}
          components={data.components}
          editable={canManage && actions.canEdit}
          canStage={canManage && actions.canStage}
          editing={editingComponents}
          onEditingChange={setEditingComponents}
          onStage={setStaging}
          onSaved={refresh}
        />

        <Card>
          <CardHeader>
            <h2 className="font-display text-lg font-semibold text-ink">{t('orders.detail.outputsTitle')}</h2>
          </CardHeader>
          <CardBody className="overflow-x-auto">
            {data.outputs.length === 0 ? (
              <p className="text-sm text-ink-muted">{t('orders.detail.noOutputs')}</p>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('orders.outputs.lotNo')}</Th>
                    <Th className="text-right">{t('orders.outputs.qty')}</Th>
                    <Th className="text-right">{t('orders.outputs.cartons')}</Th>
                    <Th>{t('orders.outputs.producedAt')}</Th>
                    <Th>{t('orders.outputs.by')}</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {data.outputs.map((output) => (
                    <Tr key={output.id}>
                      <Td className="font-mono text-sm">{output.lotNo}</Td>
                      <Td className="text-right">{formatQty(output.qtyBase)}</Td>
                      <Td className="text-right">{output.cartons ?? '—'}</Td>
                      <Td>{formatDateTime(output.producedAt)}</Td>
                      <Td>{output.byUserName ?? '—'}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="font-display text-lg font-semibold text-ink">
              {t('orders.detail.traceabilityTitle')}
            </h2>
            <p className="mt-1 text-sm text-ink-muted">{t('orders.detail.traceabilitySubtitle')}</p>
          </CardHeader>
          <CardBody className="overflow-x-auto">
            {(trace.data?.consumed.length ?? 0) === 0 ? (
              <p className="text-sm text-ink-muted">{t('orders.detail.noConsumption')}</p>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('orders.traceability.at')}</Th>
                    <Th>{t('orders.traceability.article')}</Th>
                    <Th>{t('orders.traceability.lot')}</Th>
                    <Th>{t('orders.traceability.bestBefore')}</Th>
                    <Th>{t('orders.traceability.bin')}</Th>
                    <Th className="text-right">{t('orders.traceability.qty')}</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {trace.data?.consumed.map((line) => (
                    <Tr key={line.movementId}>
                      <Td>{formatDateTime(line.at)}</Td>
                      <Td>
                        <div>{line.articleName}</div>
                        <div className="font-mono text-xs text-ink-muted">{line.articleNo}</div>
                      </Td>
                      <Td className="font-mono text-sm">{line.lotNo ?? '—'}</Td>
                      <Td>{line.bestBefore ? formatDate(line.bestBefore) : '—'}</Td>
                      <Td className="font-mono text-sm">{line.binCode ?? '—'}</Td>
                      <Td className="text-right">{formatQty(line.qtyBase)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </div>

      {completing ? (
        <CompleteModal
          orderId={id}
          defaultLotNo={data.orderNo}
          onClose={() => {
            setCompleting(false);
          }}
          onDone={refresh}
        />
      ) : null}

      {staging ? (
        <StageModal
          orderId={id}
          component={staging}
          onClose={() => {
            setStaging(null);
          }}
          onDone={refresh}
        />
      ) : null}

      {cancelling ? (
        <ConfirmDialog
          title={t('orders.confirm.cancelTitle')}
          message={t('orders.confirm.cancelMessage')}
          variant="danger"
          confirmLabel={t('orders.confirm.cancelConfirm')}
          cancelLabel={t('orders.form.cancel')}
          onClose={() => {
            setCancelling(false);
          }}
          onConfirm={async () => {
            try {
              await cancelOrder(id);
              toastSuccess(t('orders.toasts.cancelled'));
              refresh();
              setCancelling(false);
              void navigate('/production/orders');
            } catch (err) {
              toastError(err);
            }
          }}
        />
      ) : null}
    </>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="min-w-0">
      <div className="font-mono text-[0.7rem] uppercase tracking-[0.14em] text-ink-muted">{label}</div>
      <div className="mt-1 truncate text-sm text-ink">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// components card
// ---------------------------------------------------------------------------

interface DraftComponent {
  key: string;
  articleId: string;
  qtyBase: string;
}

function ComponentsCard({
  orderId,
  components,
  editable,
  canStage,
  editing,
  onEditingChange,
  onStage,
  onSaved,
}: {
  orderId: string;
  components: ProductionOrderComponent[];
  editable: boolean;
  canStage: boolean;
  editing: boolean;
  onEditingChange: (value: boolean) => void;
  onStage: (component: ProductionOrderComponent) => void;
  onSaved: () => void;
}): ReactNode {
  const { t } = useTranslation('production');
  const [draft, setDraft] = useState<DraftComponent[]>([]);

  function beginEdit(): void {
    setDraft(
      components.map((component) => ({
        key: component.id,
        articleId: component.articleId,
        qtyBase: component.qtyBase,
      })),
    );
    onEditingChange(true);
  }

  const save = useMutation({
    mutationFn: () =>
      setComponents(
        orderId,
        draft
          .filter((row) => row.articleId && row.qtyBase.trim())
          .map((row) => ({ articleId: row.articleId, qtyBase: row.qtyBase.trim() })),
      ),
    onSuccess: () => {
      toastSuccess(t('orders.toasts.componentsSaved'));
      onEditingChange(false);
      onSaved();
    },
    onError: (err) => {
      toastError(err);
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-ink">{t('orders.detail.componentsTitle')}</h2>
          <p className="mt-1 max-w-xl text-sm text-ink-muted">{t('orders.detail.componentsSubtitle')}</p>
        </div>
        {editable && !editing ? (
          <Button variant="secondary" size="sm" onClick={beginEdit}>
            {t('orders.actions.edit')}
          </Button>
        ) : null}
      </CardHeader>
      <CardBody className="overflow-x-auto">
        {editing ? (
          <div className="flex flex-col gap-3">
            {draft.map((row, index) => (
              <div key={row.key} className="flex flex-wrap items-end gap-3">
                <div className="min-w-[16rem] flex-1">
                  <ArticlePicker
                    label={t('orders.components.article')}
                    value={row.articleId}
                    onChange={(articleId) => {
                      setDraft((current) =>
                        current.map((item, i) => (i === index ? { ...item, articleId } : item)),
                      );
                    }}
                  />
                </div>
                <div className="w-40">
                  <FormField
                    label={t('orders.components.required')}
                    inputMode="decimal"
                    value={row.qtyBase}
                    onChange={(event) => {
                      const { value } = event.target;
                      setDraft((current) =>
                        current.map((item, i) => (i === index ? { ...item, qtyBase: value } : item)),
                      );
                    }}
                  />
                </div>
                <Button
                  variant="ghost"
                  icon={<Trash2 className="size-4" />}
                  aria-label={t('orders.components.remove')}
                  onClick={() => {
                    setDraft((current) => current.filter((_, i) => i !== index));
                  }}
                >
                  {t('orders.components.remove')}
                </Button>
              </div>
            ))}

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                icon={<Plus className="size-4" />}
                onClick={() => {
                  setDraft((current) => [
                    ...current,
                    { key: `new-${current.length}-${Date.now()}`, articleId: '', qtyBase: '' },
                  ]);
                }}
              >
                {t('orders.components.add')}
              </Button>
              <div className="ml-auto flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    onEditingChange(false);
                  }}
                >
                  {t('orders.form.cancel')}
                </Button>
                <Button
                  loading={save.isPending}
                  onClick={() => {
                    save.mutate();
                  }}
                >
                  {t('orders.components.save')}
                </Button>
              </div>
            </div>
          </div>
        ) : components.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('orders.detail.noComponents')}</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('orders.components.article')}</Th>
                <Th className="text-right">{t('orders.components.required')}</Th>
                <Th className="text-right">{t('orders.components.issued')}</Th>
                <Th className="text-right">{t('orders.components.outstanding')}</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {components.map((component) => (
                <Tr key={component.id}>
                  <Td>
                    <div>{component.articleName}</div>
                    <div className="font-mono text-xs text-ink-muted">{component.articleNo}</div>
                  </Td>
                  <Td className="text-right">
                    {formatQty(component.qtyBase, { unit: component.unitCode ?? undefined })}
                  </Td>
                  <Td className="text-right">{formatQty(component.issuedQtyBase)}</Td>
                  <Td className="text-right">{formatQty(component.outstandingQtyBase)}</Td>
                  <Td className="text-right">
                    {canStage ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={<Truck className="size-4" />}
                        onClick={() => {
                          onStage(component);
                        }}
                      >
                        {t('orders.components.stage')}
                      </Button>
                    ) : null}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// modals
// ---------------------------------------------------------------------------

function StageModal({
  orderId,
  component,
  onClose,
  onDone,
}: {
  orderId: string;
  component: ProductionOrderComponent;
  onClose: () => void;
  onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('production');
  const [qtyBase, setQtyBase] = useState(component.outstandingQtyBase);

  const mutation = useMutation({
    mutationFn: () => stageComponent(orderId, component.id, { qtyBase }),
    onSuccess: () => {
      toastSuccess(t('orders.toasts.staged'));
      onDone();
      onClose();
    },
    onError: (err) => {
      toastError(err);
    },
  });

  return (
    <Modal open onClose={onClose} title={t('orders.components.stageTitle')} subtitle={component.articleName} width={460}>
      <div className="flex flex-col gap-4">
        <FormField
          label={t('orders.components.stageQty')}
          inputMode="decimal"
          value={qtyBase}
          onChange={(event) => {
            setQtyBase(event.target.value);
          }}
        />
        <p className="text-sm text-ink-muted">{t('orders.components.stageAuto')}</p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('orders.form.cancel')}
          </Button>
          <Button
            loading={mutation.isPending}
            onClick={() => {
              mutation.mutate();
            }}
          >
            {t('orders.components.stage')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CompleteModal({
  orderId,
  defaultLotNo,
  onClose,
  onDone,
}: {
  orderId: string;
  defaultLotNo: string;
  onClose: () => void;
  onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('production');
  const [producedQtyBase, setProducedQtyBase] = useState('');
  const [lotNo, setLotNo] = useState(defaultLotNo);
  const [bestBefore, setBestBefore] = useState('');
  const [cartons, setCartons] = useState('');
  const [createPallet, setCreatePallet] = useState(false);
  const [autoConsume, setAutoConsume] = useState(true);

  const mutation = useMutation({
    mutationFn: () =>
      completeOrder(orderId, {
        producedQtyBase,
        ...(lotNo.trim() ? { lotNo: lotNo.trim() } : {}),
        ...(bestBefore ? { bestBefore } : {}),
        ...(cartons.trim() ? { cartons: Number(cartons) } : {}),
        createPallet,
        autoConsume,
      }),
    onSuccess: () => {
      toastSuccess(t('orders.toasts.completed'));
      onDone();
      onClose();
    },
    onError: (err) => {
      toastError(err);
    },
  });

  return (
    <Modal open onClose={onClose} title={t('orders.complete.title')} width={520}>
      <div className="flex flex-col gap-4">
        <FormField
          label={t('orders.complete.producedQty')}
          inputMode="decimal"
          value={producedQtyBase}
          onChange={(event) => {
            setProducedQtyBase(event.target.value);
          }}
        />
        <FormField
          label={t('orders.complete.lotNo')}
          hint={<span className="text-xs text-ink-muted">{t('orders.complete.lotNoHint')}</span>}
          value={lotNo}
          onChange={(event) => {
            setLotNo(event.target.value);
          }}
        />
        <FormField
          label={t('orders.complete.bestBefore')}
          type="date"
          value={bestBefore}
          onChange={(event) => {
            setBestBefore(event.target.value);
          }}
        />
        <FormField
          label={t('orders.complete.cartons')}
          inputMode="numeric"
          value={cartons}
          onChange={(event) => {
            setCartons(event.target.value);
          }}
        />

        <label className="flex items-center gap-2 text-sm text-ink">
          <Input
            type="checkbox"
            className="w-auto"
            inputClassName="size-4 p-0"
            checked={createPallet}
            onChange={(event) => {
              setCreatePallet(event.target.checked);
            }}
          />
          {t('orders.complete.createPallet')}
        </label>
        <label className="flex items-center gap-2 text-sm text-ink">
          <Input
            type="checkbox"
            className="w-auto"
            inputClassName="size-4 p-0"
            checked={autoConsume}
            onChange={(event) => {
              setAutoConsume(event.target.checked);
            }}
          />
          {t('orders.complete.autoConsume')}
        </label>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('orders.form.cancel')}
          </Button>
          <Button
            loading={mutation.isPending}
            onClick={() => {
              mutation.mutate();
            }}
          >
            {t('orders.complete.submit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
